import * as React from 'react'
import { jt, t } from 'ttag'
import { GoalWithProgress, Lang, ProgressType } from '../../services/api.types'
import dayjs from '../../dayjsConfig'
import { CustomTypeCategory, getCustomTypeText, getCustomUnitText } from '../../services/CustomTypes'
import {
  getChallengeProgressUnits,
  getCollectiveGoalValue,
  isCollectiveType,
  isCustomType,
  isSimpleDayType,
} from './challenges'
import { currentLang, currentLocale } from '../../i18nInit'
import { unitsCountDurationMinuteLong, unitsCountDurationMinuteShort, unitsCountEnergyCalorieLong } from './units'
import { GoalConfig } from '../../views/ChallengeAdmin/ChallengeAdmin'

export function formatThousands(num: number | string | null | undefined, minimumFractionDigits?: number): string {
  // Allow numeric strings from API responses without breaking formatting.
  const parsedValue = typeof num === 'number' ? num : num ? Number(num) : 0
  // Fall back to 0 when the value is NaN or infinite.
  const value = Number.isFinite(parsedValue) ? parsedValue : 0
  // Only set fraction digits when a value is provided to avoid surprising defaults.
  const options: Intl.NumberFormatOptions = {}
  if (minimumFractionDigits !== undefined) {
    options.minimumFractionDigits = minimumFractionDigits
    options.maximumFractionDigits = minimumFractionDigits
  }
  return value.toLocaleString(currentLocale, options)
}

export function getProgressTypeString(goal: GoalWithProgress, short?: boolean) {
  const {
    progressType,
    stepsGoal,
    activityFormulaTarget,
    dailyPersistenceGoal: dailyPersistenceGoalOrig,
    persistenceGoal,
    startDate,
    endDate,
    customType,
    customUnit,
    customName,
    customGoal,
    id,
  } = goal
  // Get collective goal value, checking goals.collective first (new format) or collectiveGoal (old format)
  const collectiveGoalValue = isCollectiveType(progressType) ? getCollectiveGoalValue(goal) : 0
  const targetNumber = activityFormulaTarget || 100
  let stepsNumber = formatThousands(progressType === ProgressType.COLLECTIVE_STEPS ? collectiveGoalValue : stepsGoal)
  const caloriesNumber = formatThousands(collectiveGoalValue)
  const minutesNumber = formatThousands(collectiveGoalValue)
  const collectiveGoalNumber = formatThousands(collectiveGoalValue)
  const dailyPersistenceGoal =
    progressType === ProgressType.PERSISTENCE_ALL
      ? dailyPersistenceGoalOrig
      : formatThousands(dailyPersistenceGoalOrig || 0)
  const daysTotal = dayjs(endDate).diff(dayjs(startDate), 'days') + 1

  const customTotal = formatThousands(dailyPersistenceGoalOrig || customGoal || 0) //daysTotal * dailyPersistenceGoalOrig

  const isCustom = isCustomType(progressType)
  const activity = isCustom ? getCustomTypeText(customType, customName) : ''
  //const unit = isCustom ? getCustomUnitText(customType, customType, customUnit) : ''
  const unit = getChallengeProgressUnits(goal)

  // For Bolt campaign.
  if (id === 'aa85857a-6f30-4ab2-a6a7-751cc96b3196' && (stepsGoal === 186004 || stepsGoal === 185978)) {
    stepsNumber = formatThousands(186000)
  }

  switch (progressType) {
    case ProgressType.ALL:
      return t`${targetNumber}% of activity`
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return t`${stepsNumber} steps`
    case ProgressType.COLLECTIVE_CALORIES:
      return `${caloriesNumber} ${unitsCountEnergyCalorieLong}`
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return `${minutesNumber} ${unitsCountDurationMinuteShort}`
    //return `${minutesNumber} ${unitsCountDurationMinuteLong}`
    case ProgressType.PERSISTENCE_STEPS:
      return t`${dailyPersistenceGoal} steps a day for ${persistenceGoal}/${daysTotal} days`
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return t`${dailyPersistenceGoal} minutes a day for ${persistenceGoal}/${daysTotal} days`
    case ProgressType.PERSISTENCE_ALL:
      return t`${dailyPersistenceGoal}% of activity a day for ${persistenceGoal}/${daysTotal} days`
    case ProgressType.CUSTOM:
    case ProgressType.INTENSIVE_MINUTES:
      return short ? `${customTotal} ${unit}` : `${activity} ${customTotal} ${unit}`
    case ProgressType.COLLECTIVE_CUSTOM:
      return `${collectiveGoalNumber} ${unit}`
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      if (short) {
        return `${dailyPersistenceGoal} ${unit}`
      } else {
        // translator: {Sleeping} {8} {h} a day for {15}/{30} days
        return t`${activity} ${dailyPersistenceGoal} ${unit} a day for ${persistenceGoal}/${daysTotal} days`
      }
    default:
      return 'x'
  }
}

export function getDailyGoalStringLegacy({
  progressType,
  dailyPersistenceGoal: dailyPersistenceGoalOrig,
  customType,
  customUnit,
  customName,
  simpleManualEntry,
}: GoalWithProgress) {
  const dailyPersistenceGoal =
    progressType === ProgressType.PERSISTENCE_ALL
      ? dailyPersistenceGoalOrig
      : formatThousands(dailyPersistenceGoalOrig || 0)

  const isCustom = isCustomType(progressType)
  const activity = isCustom ? getCustomTypeText(customType, customName) : ''
  const unit = isCustom ? getCustomUnitText(customType, customType, customUnit) : ''

  switch (progressType) {
    case ProgressType.PERSISTENCE_STEPS:
      return `${dailyPersistenceGoal} ${t`steps`}`
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return `${dailyPersistenceGoal} ${unitsCountDurationMinuteShort}`
    case ProgressType.PERSISTENCE_ALL:
      return `${dailyPersistenceGoal}% ${t`of activity`}`
    case ProgressType.PERSISTENCE_CUSTOM:
      return isSimpleDayType(customType as unknown as CustomTypeCategory)
        ? `${activity}`
        : `${dailyPersistenceGoal} ${unit}`
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return `${activity}`
    default:
      return ''
  }
}

export function getDailyGoalString(
  {
    progressType,
    dailyPersistenceGoal: dailyPersistenceGoalOrig,
    customType,
    customUnit,
    customName,
    simpleManualEntry,
  }: GoalWithProgress,
  goalConfig: GoalConfig | null | undefined,
) {
  //const dailyPersistenceGoal = goalConfig?.dailyGoal ? goalConfig.dailyGoal : dailyPersistenceGoalOrig
  const dailyPersistenceGoal = dailyPersistenceGoalOrig

  const isCustom = isCustomType(progressType)
  const activity = isCustom ? getCustomTypeText(customType, customName) : ''
  const unit = isCustom ? getCustomUnitText(customType, customType, customUnit) : ''

  switch (progressType) {
    case ProgressType.PERSISTENCE_STEPS:
      return `${dailyPersistenceGoal} ${t`steps`}`
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return `${dailyPersistenceGoal} ${unitsCountDurationMinuteShort}`
    case ProgressType.PERSISTENCE_ALL:
      return `${dailyPersistenceGoal}% ${t`of activity`}`
    case ProgressType.PERSISTENCE_CUSTOM:
      return isSimpleDayType(customType as unknown as CustomTypeCategory)
        ? `${activity}`
        : `${formatThousands(dailyPersistenceGoal)} ${unit}`
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return `${activity}`
    default:
      return ''
  }
}

export function getFullName(firstName: string, lastName: string, email: string): string {
  return !firstName && !lastName ? email : `${firstName} ${lastName}`
}

/**
 * True for requests that never got a response — offline, DNS failure, corporate firewall,
 * timeout. Distinct from the server answering with an error: navigator.onLine (and the offline
 * snackbar built on it) only catches the no-network-interface case, so this classification is
 * how connectivity problems on "online" devices are recognized.
 */
export function isNetworkError(error: any): boolean {
  return (
    !!error &&
    !error.response &&
    (error.isAxiosError || error.message === 'Network Error' || error.code === 'ECONNABORTED')
  )
}

/**
 * Error message for a failed request: connectivity problems get an accurate, actionable
 * message (most of our users are on corporate networks where a firewall is the usual cause);
 * everything else falls back to the generic support message via getErrorMsg.
 */
export function getRequestErrorMsg(error: any, reason?: string): JSX.Element {
  if (!isNetworkError(error)) {
    return getErrorMsg(reason)
  }
  const email = currentLang === Lang.ET ? 'tugi@yumuuv.com' : 'support@yumuuv.com'
  const supportEmail = (
    <a key={'supportEmail'} href={'mailto:' + email}>
      {email}
    </a>
  )
  return (
    <div>
      {t`We can't reach the YuMuuv servers right now. Please check your internet connection — on a work network, a firewall may be blocking access.`}{' '}
      {jt`If the problem persists, contact ${supportEmail}.`}
    </div>
  )
}

export function getErrorMsg(reason?: string): JSX.Element {
  const email = currentLang === Lang.ET ? 'tugi@yumuuv.com' : 'support@yumuuv.com'
  const supportEmail = (
    <a key={'supportEmail'} href={'mailto:' + email}>
      {email}
    </a>
  )

  if (reason && (typeof reason !== 'string' || reason.includes('SQL'))) {
    reason = undefined
  }

  return (
    <div>
      {jt`Something went wrong. Please try again or contact our support at ${supportEmail}`}
      {reason ? (
        <>
          {' '}
          <i style={{ whiteSpace: 'nowrap' }}>({reason})</i>
        </>
      ) : (
        ''
      )}
    </div>
  )
}
